// C:\Users\aleja\Code\inventory_analytics\src\front\src\pages\OrmaWarehouseValuation.jsx
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Context } from "../store/appContext";
import "../styles/layout.css";
import "../styles/utilities.css";
import "../styles/forms.css";
import "../styles/ormaValuation.css";

const EMPTY_CONFIG = {
  branch_prefix: "",
  warehouse: "",
  exclude_suffix_u: false,
  suffix_r_min_cost: "",
  excluded_material_ax_groups: [],
};

const DECIMAL_PATTERN = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/;

const currencyFormatter = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
});

const integerFormatter = new Intl.NumberFormat("es-ES", {
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat("es-ES", {
  maximumFractionDigits: 6,
});

const EXCLUSION_ROWS = [
  ["excluded_material_suffix_u", "Sufijo U"],
  ["invalid_cost", "Coste inválido"],
  ["excluded_material_r_below_threshold", "Sufijo R bajo el umbral"],
  ["excluded_missing_master", "Sin coincidencia MAESTRO"],
  ["excluded_missing_ax_group", "Sin grupo AX"],
  ["excluded_ax_group", "Grupo AX excluido"],
];

const DETAIL_CLASSIFICATION_LABELS = {
  included: "Filas incluidas",
  excluded_material_suffix_u: "Exclusiones por sufijo U",
  invalid_cost: "Exclusiones por coste inválido",
  excluded_material_r_below_threshold: "Exclusiones por sufijo R bajo el umbral",
  excluded_missing_master: "Exclusiones sin coincidencia MAESTRO",
  excluded_missing_ax_group: "Exclusiones sin grupo AX",
  excluded_ax_group: "Exclusiones por grupo AX",
};

const DETAIL_FILTER_FIELDS = [
  ["material_id", "ID material"],
  ["description", "Descripción"],
  ["family", "Familia"],
  ["brand", "Marca"],
  ["id_material_ax", "Grupo AX"],
  ["branch_name", "Sucursal"],
  ["warehouse", "Almacén"],
  ["location", "Ubicación"],
];

const DETAIL_SORT_OPTIONS = [
  ["material_id", "ID material"],
  ["description", "Descripción"],
  ["branch_name", "Sucursal"],
  ["warehouse", "Almacén"],
  ["family", "Familia"],
  ["brand", "Marca"],
  ["id_material_ax", "Grupo AX"],
  ["cost", "Coste"],
  ["quantity", "Cantidad auxiliar"],
];

const EMPTY_DETAIL_FILTERS = {
  material_id: "",
  description: "",
  family: "",
  brand: "",
  id_material_ax: "",
  branch_name: "",
  warehouse: "",
  location: "",
};

const EMPTY_DETAIL_PAGINATION = {
  page: 1,
  per_page: 25,
  total_items: 0,
  total_pages: 0,
};

const INITIAL_DETAIL_STATE = {
  isOpen: false,
  classification: "",
  status: "idle",
  items: [],
  pagination: EMPTY_DETAIL_PAGINATION,
  draftFilters: EMPTY_DETAIL_FILTERS,
  appliedFilters: {},
  sortBy: "material_id",
  sortOrder: "asc",
  error: "",
  generatedAt: null,
  responseLoad: null,
  config: null,
  calculationLoadId: null,
};

const TABS = [
  { key: "resumen", label: "Resumen" },
  { key: "exclusiones", label: "Exclusiones" },
  { key: "grupos-ax", label: "Grupos AX" },
  { key: "familias", label: "Familias" },
  { key: "materiales", label: "Materiales" },
];

const hasDisplayValue = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

const formatCurrency = (value) => {
  if (!hasDisplayValue(value)) return "—";
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? currencyFormatter.format(numericValue) : "—";
};

const formatInteger = (value) => {
  if (!hasDisplayValue(value)) return "—";
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? integerFormatter.format(numericValue) : "—";
};

const formatNumber = (value) => {
  if (!hasDisplayValue(value)) return "—";
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numberFormatter.format(numericValue) : "—";
};

const formatPercentage = (value) => {
  if (!hasDisplayValue(value) || !Number.isFinite(Number(value))) return "—";
  return `${String(value).replace(".", ",")} %`;
};

const formatText = (value, emptyLabel = "—") =>
  hasDisplayValue(value) ? String(value) : emptyLabel;

const formatDuration = (value) => {
  const formatted = formatInteger(value);
  return formatted === "—" ? formatted : `${formatted} ms`;
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-ES", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const copyAppliedConfig = (config) => ({
  branch_prefix: String(config?.branch_prefix ?? ""),
  exclude_suffix_u: config?.exclude_suffix_u === true,
  excluded_material_ax_groups: Array.isArray(config?.excluded_material_ax_groups)
    ? config.excluded_material_ax_groups.map((value) => String(value))
    : [],
  suffix_r_min_cost: String(config?.suffix_r_min_cost ?? ""),
  warehouse: String(config?.warehouse ?? ""),
});

const normalizeDetailFilters = (filters) =>
  DETAIL_FILTER_FIELDS.reduce((normalized, [key]) => {
    const value = String(filters?.[key] ?? "").trim();
    if (value) normalized[key] = value;
    return normalized;
  }, {});

const normalizeDetailPagination = (pagination, fallbackPerPage) => {
  const page = Number(pagination?.page);
  const perPage = Number(pagination?.per_page);
  const totalItems = Number(pagination?.total_items);
  const totalPages = Number(pagination?.total_pages);
  return {
    page: Number.isInteger(page) && page >= 1 ? page : 1,
    per_page:
      Number.isInteger(perPage) && perPage >= 1 && perPage <= 100
        ? perPage
        : fallbackPerPage,
    total_items: Number.isInteger(totalItems) && totalItems >= 0 ? totalItems : 0,
    total_pages: Number.isInteger(totalPages) && totalPages >= 0 ? totalPages : 0,
  };
};

const configFromDefaults = (defaults) => ({
  branch_prefix: String(defaults?.branch_prefix ?? ""),
  warehouse: String(defaults?.warehouse ?? ""),
  exclude_suffix_u: defaults?.exclude_suffix_u === true,
  suffix_r_min_cost: String(defaults?.suffix_r_min_cost ?? ""),
  excluded_material_ax_groups: Array.isArray(defaults?.excluded_material_ax_groups)
    ? defaults.excluded_material_ax_groups.map((value) => String(value))
    : [],
});

const normalizeConfig = (config) => ({
  branch_prefix: String(config.branch_prefix ?? "").trim(),
  warehouse: String(config.warehouse ?? "").trim(),
  exclude_suffix_u: config.exclude_suffix_u === true,
  suffix_r_min_cost: String(config.suffix_r_min_cost ?? "").trim(),
  excluded_material_ax_groups: Array.from(
    new Set(
      (Array.isArray(config.excluded_material_ax_groups)
        ? config.excluded_material_ax_groups
        : []
      )
        .map((value) => String(value).trim())
        .filter(Boolean),
    ),
  ),
});

const configsMatch = (draft, applied) => {
  if (!applied) return true;
  const draftGroups = [...new Set((draft.excluded_material_ax_groups || []).map(String))].sort();
  const appliedGroups = [
    ...new Set((applied.excluded_material_ax_groups || []).map(String)),
  ].sort();

  return (
    String(draft.branch_prefix ?? "").trim() ===
      String(applied.branch_prefix ?? "").trim() &&
    String(draft.warehouse ?? "").trim() === String(applied.warehouse ?? "").trim() &&
    draft.exclude_suffix_u === applied.exclude_suffix_u &&
    String(draft.suffix_r_min_cost ?? "").trim() ===
      String(applied.suffix_r_min_cost ?? "").trim() &&
    draftGroups.length === appliedGroups.length &&
    draftGroups.every((value, index) => value === appliedGroups[index])
  );
};

const getRequestError = (result, fallback) => {
  if (result?.status === 403) return "No tienes permisos para consultar esta valoración.";
  return result?.error || result?.msg || fallback;
};

const isNoUsableLoadResponse = (result) =>
  result?.status === 404 ||
  String(result?.error || "")
    .toLocaleLowerCase("es-ES")
    .includes("carga de catálogo orma usable");

const OrmaWarehouseValuation = () => {
  const { actions } = useContext(Context);
  const {
    getOrmaValuationOptions,
    calculateOrmaWarehouseValuation,
    getOrmaWarehouseValuationDetails,
    showToast,
  } = actions;
  const calculationControllerRef = useRef(null);
  const calculationInFlightRef = useRef(false);
  const detailRequestControllerRef = useRef(null);
  const detailRequestIdRef = useRef(0);
  const detailTriggerElementRef = useRef(null);
  const detailModalElementRef = useRef(null);
  const detailCloseButtonRef = useRef(null);
  const previousBodyOverflowRef = useRef("");
  const tabListRef = useRef(null);

  const [optionsAttempt, setOptionsAttempt] = useState(0);
  const [accessBlocked, setAccessBlocked] = useState(false);
  const [optionsState, setOptionsState] = useState({
    status: "idle",
    data: null,
    error: "",
  });
  const [draftConfig, setDraftConfig] = useState(EMPTY_CONFIG);
  const [appliedConfig, setAppliedConfig] = useState(null);
  const [calculationState, setCalculationState] = useState({
    status: "idle",
    result: null,
    error: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [detailState, setDetailState] = useState(INITIAL_DETAIL_STATE);
  const [activeTab, setActiveTab] = useState("resumen");
  const [materialsView, setMaterialsView] = useState("included");
  const [sourceDataOpen, setSourceDataOpen] = useState(false);
  const [axPanelOpen, setAxPanelOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const loadOptions = async () => {
      setOptionsState((current) => ({ ...current, status: "loading", error: "" }));
      const result = await getOrmaValuationOptions({ signal: controller.signal });
      if (!active || result?.aborted) return;

      if (!result.success) {
        if (isNoUsableLoadResponse(result)) {
          setOptionsState({ status: "noUsableLoad", data: null, error: "Sin carga ORMA usable" });
          return;
        }

        const message = getRequestError(
          result,
          "No se pudieron cargar las opciones de valoración ORMA.",
        );
        if (result.status === 403) setAccessBlocked(true);
        setOptionsState({ status: "error", data: null, error: message });
        if (result.status !== 401) showToast(message, "error");
        return;
      }

      if (!result.data?.load || !result.data?.defaults) {
        setOptionsState({
          status: "noUsableLoad",
          data: result.data || null,
          error: "Sin carga ORMA usable",
        });
        return;
      }

      setDraftConfig(configFromDefaults(result.data.defaults));
      setFieldErrors({});
      setOptionsState({ status: "ready", data: result.data, error: "" });
    };

    loadOptions();
    return () => {
      active = false;
      controller.abort();
    };
  }, [getOrmaValuationOptions, optionsAttempt, showToast]);

  useEffect(
    () => () => {
      calculationControllerRef.current?.abort();
      detailRequestControllerRef.current?.abort();
    },
    [],
  );

  const options = optionsState.data;
  const branches = Array.isArray(options?.branches) ? options.branches : [];
  const warehouses = Array.isArray(options?.warehouses) ? options.warehouses : [];
  const axGroups = Array.isArray(options?.ax_groups) ? options.ax_groups : [];
  const defaultBranch = String(options?.defaults?.branch_prefix ?? "");
  const matchingDefaultBranches = branches.filter((branch) =>
    String(branch?.value ?? "").startsWith(defaultBranch),
  );
  const defaultBranchLabel =
    defaultBranch && matchingDefaultBranches.length === 1
      ? String(matchingDefaultBranches[0].value)
      : defaultBranch;
  const visibleAxCodes = new Set(axGroups.map((group) => String(group?.value ?? "")));
  const visibleExcludedCount = draftConfig.excluded_material_ax_groups.filter((code) =>
    visibleAxCodes.has(String(code)),
  ).length;
  const isConfigDirty = useMemo(
    () => Boolean(appliedConfig) && !configsMatch(draftConfig, appliedConfig),
    [appliedConfig, draftConfig],
  );
  const formDisabled = optionsState.status !== "ready" || accessBlocked;
  const calculating = calculationState.status === "loading";

  const updateDraft = (field, value) => {
    setDraftConfig((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) => ({ ...current, [field]: "" }));
  };

  const toggleAxGroup = (code) => {
    setDraftConfig((current) => {
      const selected = current.excluded_material_ax_groups.includes(code);
      return {
        ...current,
        excluded_material_ax_groups: selected
          ? current.excluded_material_ax_groups.filter((value) => value !== code)
          : [...current.excluded_material_ax_groups, code],
      };
    });
  };

  const restoreAllDefaults = () => {
    if (!options?.defaults) return;
    setDraftConfig(configFromDefaults(options.defaults));
    setFieldErrors({});
  };

  const restoreAxDefaults = () => {
    const defaults = Array.isArray(options?.defaults?.excluded_material_ax_groups)
      ? options.defaults.excluded_material_ax_groups.map((value) => String(value))
      : [];
    setDraftConfig((current) => ({
      ...current,
      excluded_material_ax_groups: [...defaults],
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (formDisabled || calculating || calculationInFlightRef.current) return;

    const normalized = normalizeConfig(draftConfig);
    const errors = {};
    if (!normalized.branch_prefix) errors.branch_prefix = "Selecciona una sucursal.";
    if (!normalized.warehouse) errors.warehouse = "Selecciona un almacén.";
    if (!normalized.suffix_r_min_cost) {
      errors.suffix_r_min_cost = "El umbral R es obligatorio.";
    } else if (!DECIMAL_PATTERN.test(normalized.suffix_r_min_cost)) {
      errors.suffix_r_min_cost = "Introduce un decimal válido usando punto como separador.";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    calculationControllerRef.current?.abort();
    const controller = new AbortController();
    calculationControllerRef.current = controller;
    calculationInFlightRef.current = true;
    setCalculationState((current) => ({ ...current, status: "loading", error: "" }));

    const response = await calculateOrmaWarehouseValuation(normalized, {
      signal: controller.signal,
    });
    calculationInFlightRef.current = false;
    if (response?.aborted) return;

    if (!response.success || !response.data?.config) {
      let message;
      if (response.status === 404) {
        message = "La carga ORMA ya no está disponible. Recarga la información antes de calcular.";
        setOptionsState((current) => ({
          ...current,
          status: "noUsableLoad",
          error: "Sin carga ORMA usable",
        }));
      } else {
        message = getRequestError(response, "No se pudo calcular la valoración del almacén.");
      }
      if (response.status === 403) setAccessBlocked(true);
      setCalculationState((current) => ({ ...current, status: "error", error: message }));
      if (response.status !== 401) showToast(message, "error");
      return;
    }

    setAppliedConfig(response.data.config);
    setCalculationState({ status: "success", result: response.data, error: "" });
  };

  const load = options?.load || {};
  const coverage = options?.coverage || {};
  const result = calculationState.result;
  const valuation = result?.valuation || {};
  const summary = result?.summary || {};
  const quality = result?.quality || {};
  const classifications = result?.classifications || {};
  const breakdowns = result?.breakdowns || {};
  const byAxGroup = Array.isArray(breakdowns.by_ax_group) ? breakdowns.by_ax_group : [];
  const byFamily = Array.isArray(breakdowns.by_family) ? breakdowns.by_family : [];
  const topMaterials = Array.isArray(breakdowns.top_materials) ? breakdowns.top_materials : [];
  const warnings = Array.isArray(result?.warnings) ? result.warnings : [];
  const hasConsistencyIssue = Boolean(
    result?.consistency &&
      (result.consistency.classification_total_matches !== true ||
        result.consistency.in_scope_total_matches !== true),
  );
  const hasNoIncludedRows =
    summary.included_rows === 0 || String(summary.included_rows) === "0";

  const requestDetail = useCallback(
    async ({
      classification,
      page,
      perPage,
      filters,
      sortBy,
      sortOrder,
      config,
      calculationLoadId,
      resetItems = false,
    }) => {
      if (!DETAIL_CLASSIFICATION_LABELS[classification] || !config) return;

      detailRequestControllerRef.current?.abort();
      const controller = new AbortController();
      const requestId = detailRequestIdRef.current + 1;
      detailRequestIdRef.current = requestId;
      detailRequestControllerRef.current = controller;

      const normalizedPage = Math.max(1, Number.parseInt(page, 10) || 1);
      const normalizedPerPage = [25, 50, 100].includes(Number(perPage))
        ? Number(perPage)
        : 25;
      const normalizedSortBy = DETAIL_SORT_OPTIONS.some(([value]) => value === sortBy)
        ? sortBy
        : "material_id";
      const normalizedSortOrder = sortOrder === "desc" ? "desc" : "asc";
      const normalizedFilters = normalizeDetailFilters(filters);
      const configSnapshot = copyAppliedConfig(config);

      setDetailState((current) => ({
        ...current,
        isOpen: true,
        classification,
        status: "loading",
        items: resetItems ? [] : current.items,
        pagination: {
          ...current.pagination,
          page: normalizedPage,
          per_page: normalizedPerPage,
        },
        appliedFilters: { ...normalizedFilters },
        sortBy: normalizedSortBy,
        sortOrder: normalizedSortOrder,
        error: "",
        config: configSnapshot,
        calculationLoadId,
      }));

      const response = await getOrmaWarehouseValuationDetails(
        {
          config: copyAppliedConfig(configSnapshot),
          classification,
          page: normalizedPage,
          per_page: normalizedPerPage,
          filters: { ...normalizedFilters },
          sort_by: normalizedSortBy,
          sort_order: normalizedSortOrder,
        },
        { signal: controller.signal },
      );

      if (response?.aborted || detailRequestIdRef.current !== requestId) return;

      if (!response.success) {
        let message = response.error || "No se pudo cargar el detalle justificativo.";
        if (response.status === 403) {
          message = "No tienes permisos para consultar este detalle.";
        } else if (response.status === 404) {
          message = "La carga ORMA o el detalle solicitado ya no están disponibles.";
        } else if (!response.status || response.status >= 500) {
          message = "No se pudo cargar el detalle justificativo. Inténtalo de nuevo.";
        }
        setDetailState((current) => ({ ...current, status: "error", error: message }));
        if (response.status !== 401) showToast(message, "error");
        return;
      }

      const responseLoad = response.data?.load || null;
      const responseLoadId = responseLoad?.id;
      if (
        hasDisplayValue(calculationLoadId) &&
        hasDisplayValue(responseLoadId) &&
        String(calculationLoadId) !== String(responseLoadId)
      ) {
        const message =
          "La carga ORMA ha cambiado desde el cálculo. Recalcula la valoración antes de consultar el detalle.";
        setDetailState((current) => ({
          ...current,
          status: "error",
          items: [],
          error: message,
          responseLoad,
        }));
        showToast(message, "error");
        return;
      }

      const items = Array.isArray(response.data?.items) ? response.data.items : [];
      const pagination = normalizeDetailPagination(
        response.data?.pagination,
        normalizedPerPage,
      );
      setDetailState((current) => ({
        ...current,
        status: items.length === 0 ? "empty" : "success",
        items,
        pagination,
        error: "",
        generatedAt: response.data?.generated_at || null,
        responseLoad,
      }));
    },
    [getOrmaWarehouseValuationDetails, showToast],
  );

  const closeDetail = useCallback(() => {
    detailRequestControllerRef.current?.abort();
    detailRequestIdRef.current += 1;
    setDetailState((current) => ({ ...current, isOpen: false, status: "idle" }));
    window.setTimeout(() => detailTriggerElementRef.current?.focus(), 0);
  }, []);

  const openDetail = (classification, triggerElement) => {
    if (!appliedConfig || !DETAIL_CLASSIFICATION_LABELS[classification]) return;
    const configSnapshot = copyAppliedConfig(appliedConfig);
    const calculationLoadId = result?.load?.id ?? null;
    detailTriggerElementRef.current = triggerElement || document.activeElement;
    setDetailState({
      ...INITIAL_DETAIL_STATE,
      isOpen: true,
      classification,
      status: "loading",
      pagination: { ...EMPTY_DETAIL_PAGINATION },
      draftFilters: { ...EMPTY_DETAIL_FILTERS },
      appliedFilters: {},
      config: configSnapshot,
      calculationLoadId,
    });
    requestDetail({
      classification,
      page: 1,
      perPage: 25,
      filters: {},
      sortBy: "material_id",
      sortOrder: "asc",
      config: configSnapshot,
      calculationLoadId,
      resetItems: true,
    });
  };

  const updateDetailDraftFilter = (key, value) => {
    setDetailState((current) => ({
      ...current,
      draftFilters: { ...current.draftFilters, [key]: value },
    }));
  };

  const applyDetailFilters = (event) => {
    event.preventDefault();
    if (detailState.status === "loading") return;
    const filters = normalizeDetailFilters(detailState.draftFilters);
    requestDetail({
      classification: detailState.classification,
      page: 1,
      perPage: detailState.pagination.per_page,
      filters,
      sortBy: detailState.sortBy,
      sortOrder: detailState.sortOrder,
      config: detailState.config,
      calculationLoadId: detailState.calculationLoadId,
    });
  };

  const clearDetailFilters = () => {
    if (detailState.status === "loading") return;
    setDetailState((current) => ({
      ...current,
      draftFilters: { ...EMPTY_DETAIL_FILTERS },
      appliedFilters: {},
    }));
    requestDetail({
      classification: detailState.classification,
      page: 1,
      perPage: detailState.pagination.per_page,
      filters: {},
      sortBy: detailState.sortBy,
      sortOrder: detailState.sortOrder,
      config: detailState.config,
      calculationLoadId: detailState.calculationLoadId,
    });
  };

  const changeDetailSort = (sortBy, sortOrder) => {
    if (detailState.status === "loading") return;
    requestDetail({
      classification: detailState.classification,
      page: 1,
      perPage: detailState.pagination.per_page,
      filters: detailState.appliedFilters,
      sortBy,
      sortOrder,
      config: detailState.config,
      calculationLoadId: detailState.calculationLoadId,
    });
  };

  const changeDetailPerPage = (value) => {
    if (detailState.status === "loading") return;
    const perPage = Number.parseInt(value, 10);
    requestDetail({
      classification: detailState.classification,
      page: 1,
      perPage,
      filters: detailState.appliedFilters,
      sortBy: detailState.sortBy,
      sortOrder: detailState.sortOrder,
      config: detailState.config,
      calculationLoadId: detailState.calculationLoadId,
    });
  };

  const goToDetailPage = (page) => {
    if (detailState.status === "loading") return;
    const totalPages = detailState.pagination.total_pages;
    if (totalPages < 1 || page < 1 || page > totalPages) return;
    requestDetail({
      classification: detailState.classification,
      page,
      perPage: detailState.pagination.per_page,
      filters: detailState.appliedFilters,
      sortBy: detailState.sortBy,
      sortOrder: detailState.sortOrder,
      config: detailState.config,
      calculationLoadId: detailState.calculationLoadId,
    });
  };

  const retryDetail = () => {
    requestDetail({
      classification: detailState.classification,
      page: detailState.pagination.page,
      perPage: detailState.pagination.per_page,
      filters: detailState.appliedFilters,
      sortBy: detailState.sortBy,
      sortOrder: detailState.sortOrder,
      config: detailState.config,
      calculationLoadId: detailState.calculationLoadId,
    });
  };

  const getDetailAriaSort = (column) => {
    if (detailState.sortBy !== column) return "none";
    return detailState.sortOrder === "desc" ? "descending" : "ascending";
  };

  const handleTabKeyDown = useCallback((event, index) => {
    const tabs = tabListRef.current?.querySelectorAll('[role="tab"]');
    if (!tabs || tabs.length === 0) return;
    let nextIndex;
    switch (event.key) {
      case "ArrowRight":
        nextIndex = (index + 1) % tabs.length;
        break;
      case "ArrowLeft":
        nextIndex = (index - 1 + tabs.length) % tabs.length;
        break;
      case "Home":
        nextIndex = 0;
        break;
      case "End":
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    setActiveTab(TABS[nextIndex].key);
    tabs[nextIndex].focus();
  }, []);

  useEffect(() => {
    if (!detailState.isOpen) return undefined;

    previousBodyOverflowRef.current = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.setTimeout(() => detailCloseButtonRef.current?.focus(), 0);

    const handleModalKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeDetail();
        return;
      }
      if (event.key !== "Tab") return;

      const modal = detailModalElementRef.current;
      if (!modal) return;
      const focusable = Array.from(
        modal.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusable.length === 0) {
        event.preventDefault();
        modal.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleModalKeyDown);
    return () => {
      document.removeEventListener("keydown", handleModalKeyDown);
      document.body.style.overflow = previousBodyOverflowRef.current;
    };
  }, [closeDetail, detailState.isOpen]);

  const hasResult = Boolean(result && appliedConfig);

  return (
    <main className="orma-valuation-page app-container">

      {/* ── CABECERA MÍNIMA ─────────────────────────────── */}
      <header className="orma-valuation-header-compact">
        <div className="orma-header-row">
          <h1 className="orma-header-title">Valoración de almacenes</h1>
          <button
            type="button"
            className="btn btn-secondary orma-source-toggle"
            onClick={() => setSourceDataOpen((v) => !v)}
            aria-expanded={sourceDataOpen}
            aria-controls="orma-source-panel"
          >
            {sourceDataOpen ? "Ocultar datos de origen" : "Datos de origen"}
          </button>
        </div>

        {optionsState.status === "loading" && (
          <div className="orma-valuation-state orma-valuation-state-loading" aria-live="polite">
            Comprobando carga ORMA…
          </div>
        )}
        {optionsState.status === "error" && (
          <div className="orma-valuation-state orma-valuation-state-error" role="alert">
            <p>{optionsState.error}</p>
            {!accessBlocked && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setOptionsAttempt((v) => v + 1)}
              >
                Reintentar
              </button>
            )}
          </div>
        )}
        {optionsState.status === "noUsableLoad" && (
          <div className="orma-valuation-state orma-valuation-state-empty" role="status">
            <p>Sin carga ORMA usable</p>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setOptionsAttempt((v) => v + 1)}
            >
              Reintentar
            </button>
          </div>
        )}

        {sourceDataOpen && (
          <div id="orma-source-panel" className="orma-valuation-source-panel">
            <p className="orma-source-notice">
              Este resultado es una valoración operativa interna y no sustituye una valoración
              contable o fiscal.
            </p>

            {options && optionsState.status === "ready" && (
              <>
                <h3 className="orma-source-section-title">Carga ORMA</h3>
                <dl className="orma-valuation-load-grid">
                  <div><dt>Estado</dt><dd>{String(load.status ?? "—")}</dd></div>
                  <div><dt>Fecha de carga</dt><dd>{formatDate(load.loaded_at)}</dd></div>
                  <div><dt>Archivo VISOR</dt><dd title={load.visor_filename}>{load.visor_filename || "—"}</dd></div>
                  <div><dt>Archivo MAESTRO</dt><dd title={load.maestro_filename}>{load.maestro_filename || "—"}</dd></div>
                  <div><dt>Filas del catálogo</dt><dd>{formatInteger(load.catalog_rows)}</dd></div>
                  <div><dt>Cobertura MAESTRO</dt><dd>{formatPercentage(coverage.master_coverage_percent)}</dd></div>
                  <div><dt>Cobertura AX</dt><dd>{formatPercentage(coverage.ax_coverage_over_total_percent)}</dd></div>
                </dl>
              </>
            )}

            {result && appliedConfig && (
              <>
                <h3 className="orma-source-section-title">Último cálculo</h3>
                <dl className="orma-valuation-load-grid">
                  <div><dt>Fecha de cálculo</dt><dd>{formatDate(result.calculated_at)}</dd></div>
                  {hasDisplayValue(result.calculation_time_ms) && (
                    <div><dt>Tiempo de cálculo</dt><dd>{formatDuration(result.calculation_time_ms)}</dd></div>
                  )}
                  <div><dt>Moneda</dt><dd>{formatText(valuation.currency)}</dd></div>
                  <div><dt>Redondeo</dt><dd>{formatText(valuation.rounding)}</dd></div>
                  <div><dt>Decimales</dt><dd>{formatInteger(valuation.decimal_places)}</dd></div>
                </dl>

                <h3 className="orma-source-section-title">Configuración aplicada</h3>
                <dl className="orma-valuation-result-grid">
                  <div><dt>Sucursal</dt><dd>{formatText(appliedConfig.branch_prefix)}</dd></div>
                  <div><dt>Almacén</dt><dd>{formatText(appliedConfig.warehouse)}</dd></div>
                  <div><dt>Exclusión de sufijo U</dt><dd>{appliedConfig.exclude_suffix_u ? "Sí" : "No"}</dd></div>
                  <div><dt>Umbral R</dt><dd>{formatText(appliedConfig.suffix_r_min_cost)}</dd></div>
                  <div><dt>Grupos AX excluidos</dt><dd>{formatInteger(appliedConfig.excluded_material_ax_groups?.length)}</dd></div>
                </dl>
              </>
            )}
          </div>
        )}
      </header>

      {/* ── BARRA COMPACTA DE CONDICIONES ───────────────── */}
      <form
        id="orma-conditions-form"
        className="orma-valuation-conditions-bar"
        onSubmit={handleSubmit}
        noValidate
      >
        <fieldset className="orma-conditions-chips" disabled={formDisabled || calculating}>
          <div className="orma-chip orma-chip--scope">
            <label htmlFor="orma-branch" className="orma-chip-label">Sucursal</label>
            <select
              id="orma-branch"
              className="orma-chip-select"
              value={draftConfig.branch_prefix}
              onChange={(event) => updateDraft("branch_prefix", event.target.value)}
              aria-invalid={Boolean(fieldErrors.branch_prefix)}
              aria-describedby={fieldErrors.branch_prefix ? "orma-branch-error" : undefined}
              required
            >
              <option value="">Selecciona</option>
              {defaultBranch && !branches.some((b) => String(b.value) === defaultBranch) && (
                <option value={defaultBranch}>{defaultBranchLabel}</option>
              )}
              {branches.map((branch) => (
                <option key={String(branch.value)} value={String(branch.value)}>
                  {String(branch.value)} ({formatInteger(branch.rows)})
                </option>
              ))}
            </select>
            {fieldErrors.branch_prefix && (
              <span id="orma-branch-error" className="orma-chip-error">{fieldErrors.branch_prefix}</span>
            )}
          </div>

          <div className="orma-chip orma-chip--scope">
            <label htmlFor="orma-warehouse" className="orma-chip-label">Almacén</label>
            <select
              id="orma-warehouse"
              className="orma-chip-select"
              value={draftConfig.warehouse}
              onChange={(event) => updateDraft("warehouse", event.target.value)}
              aria-invalid={Boolean(fieldErrors.warehouse)}
              aria-describedby={fieldErrors.warehouse ? "orma-warehouse-error" : undefined}
              required
            >
              <option value="">Selecciona</option>
              {warehouses.map((warehouse) => (
                <option key={String(warehouse.value)} value={String(warehouse.value)}>
                  {String(warehouse.value)} ({formatInteger(warehouse.rows)})
                </option>
              ))}
            </select>
            {fieldErrors.warehouse && (
              <span id="orma-warehouse-error" className="orma-chip-error">{fieldErrors.warehouse}</span>
            )}
          </div>

          <label className="orma-chip orma-chip--rule orma-chip--toggle">
            <span className="orma-chip-label">Excluir sufijo U</span>
            <input
              id="orma-exclude-u"
              type="checkbox"
              className="orma-chip-checkbox"
              checked={draftConfig.exclude_suffix_u}
              onChange={(event) => updateDraft("exclude_suffix_u", event.target.checked)}
            />
            <span className="orma-chip-toggle-indicator" aria-hidden="true">
              {draftConfig.exclude_suffix_u ? "Sí" : "No"}
            </span>
          </label>

          <div className="orma-chip orma-chip--threshold">
            <label htmlFor="orma-r-threshold" className="orma-chip-label">Umbral R</label>
            <input
              id="orma-r-threshold"
              type="text"
              inputMode="decimal"
              className="orma-chip-input"
              value={draftConfig.suffix_r_min_cost}
              onChange={(event) => updateDraft("suffix_r_min_cost", event.target.value)}
              aria-invalid={Boolean(fieldErrors.suffix_r_min_cost)}
              aria-describedby={fieldErrors.suffix_r_min_cost ? "orma-r-error" : undefined}
              required
            />
            {fieldErrors.suffix_r_min_cost && (
              <span id="orma-r-error" className="orma-chip-error">{fieldErrors.suffix_r_min_cost}</span>
            )}
          </div>

          <button
            type="button"
            className={`orma-chip orma-chip--exclusions orma-chip--btn${axPanelOpen ? " orma-chip--btn-open" : ""}`}
            onClick={() => setAxPanelOpen((v) => !v)}
            aria-expanded={axPanelOpen}
            aria-controls="orma-ax-panel"
          >
            <span className="orma-chip-label">Grupos AX</span>
            <strong className="orma-chip-count">{visibleExcludedCount} de {axGroups.length} excluidos</strong>
          </button>

          <button
            type="button"
            className="btn btn-secondary orma-chip-action-reset"
            onClick={restoreAllDefaults}
            disabled={formDisabled || calculating}
          >
            Restablecer
          </button>
          <button
            type="submit"
            className="btn btn-primary orma-chip-action-submit"
            disabled={formDisabled || calculating || !draftConfig.warehouse}
          >
            {calculating ? "Calculando…" : "Aplicar y recalcular"}
          </button>
          {calculating && (
            <span className="visually-hidden" aria-live="polite">Calculando valoración…</span>
          )}
        </fieldset>

        {axPanelOpen && (
          <div id="orma-ax-panel" className="orma-valuation-ax-panel">
            <div className="orma-valuation-ax-heading">
              <div>
                <h3>Grupos AX excluidos</h3>
                <p>{visibleExcludedCount} de {axGroups.length} grupos excluidos</p>
              </div>
              <div className="orma-valuation-ax-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => updateDraft("excluded_material_ax_groups", [])}
                  disabled={formDisabled || calculating}
                >
                  Deseleccionar todos
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={restoreAxDefaults}
                  disabled={formDisabled || calculating}
                >
                  Restaurar defaults
                </button>
              </div>
            </div>
            <div className="orma-valuation-ax-grid">
              {axGroups.map((group) => {
                const code = String(group.value);
                return (
                  <label key={code} className="orma-valuation-ax-option">
                    <input
                      type="checkbox"
                      checked={draftConfig.excluded_material_ax_groups.includes(code)}
                      onChange={() => toggleAxGroup(code)}
                      disabled={formDisabled || calculating}
                    />
                    <span className="orma-valuation-code">{code}</span>
                    <small>{formatInteger(group.rows)} filas</small>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {(isConfigDirty || calculationState.error) && (
          <div className="orma-conditions-footer">
            {isConfigDirty && (
              <p className="orma-conditions-pending" role="status">
                Cambios pendientes de aplicar
              </p>
            )}
            {calculationState.error && (
              <div className="orma-valuation-state orma-valuation-state-error" role="alert">
                {calculationState.error}
              </div>
            )}
          </div>
        )}
      </form>

      {/* ── WORKSPACE / ESTADO VACÍO ─────────────────────── */}
      {hasResult ? (
        <div className="orma-valuation-workspace">
          <nav
            ref={tabListRef}
            className="orma-valuation-tabs"
            role="tablist"
            aria-label="Secciones de valoración"
          >
            {TABS.map((tab, index) => (
              <button
                key={tab.key}
                role="tab"
                id={`orma-tab-${tab.key}`}
                aria-selected={activeTab === tab.key}
                aria-controls={`orma-tabpanel-${tab.key}`}
                className={`orma-tab${activeTab === tab.key ? " orma-tab--active" : ""}`}
                tabIndex={activeTab === tab.key ? 0 : -1}
                onClick={() => setActiveTab(tab.key)}
                onKeyDown={(e) => handleTabKeyDown(e, index)}
              >
                {tab.label}
              </button>
            ))}
          </nav>

          <div className="orma-valuation-tab-content">

            {/* PESTAÑA: RESUMEN */}
            {activeTab === "resumen" && (
              <div
                role="tabpanel"
                id="orma-tabpanel-resumen"
                aria-labelledby="orma-tab-resumen"
                className="orma-tabpanel"
              >
                {hasNoIncludedRows && (
                  <div className="orma-valuation-no-included" role="status">
                    El cálculo se completó, pero ninguna fila quedó incluida con la configuración
                    aplicada.
                  </div>
                )}

                {/* A — Resumen de configuración aplicada */}
                {appliedConfig && (
                  <div className="orma-resumen-config-bar">
                    <span className="orma-resumen-config-item">
                      <i className="bi bi-building" aria-hidden="true" />
                      <span>{formatText(appliedConfig.branch_prefix)}</span>
                    </span>
                    <span className="orma-resumen-config-sep" aria-hidden="true">·</span>
                    <span className="orma-resumen-config-item">
                      <i className="bi bi-box-seam" aria-hidden="true" />
                      <span>{formatText(appliedConfig.warehouse)}</span>
                    </span>
                    <span className="orma-resumen-config-sep" aria-hidden="true">·</span>
                    <span className="orma-resumen-config-item">
                      <span className="orma-resumen-config-label">Sufijo U</span>
                      <span className={appliedConfig.exclude_suffix_u ? "orma-resumen-config-badge orma-resumen-config-badge--on" : "orma-resumen-config-badge"}>
                        {appliedConfig.exclude_suffix_u ? "excluido" : "incluido"}
                      </span>
                    </span>
                    <span className="orma-resumen-config-sep" aria-hidden="true">·</span>
                    <span className="orma-resumen-config-item">
                      <span className="orma-resumen-config-label">Umbral R</span>
                      <span>{formatText(appliedConfig.suffix_r_min_cost)}</span>
                    </span>
                    {appliedConfig.excluded_material_ax_groups?.length > 0 && (
                      <>
                        <span className="orma-resumen-config-sep" aria-hidden="true">·</span>
                        <span className="orma-resumen-config-item">
                          <span className="orma-resumen-config-label">Grupos AX excluidos</span>
                          <span className="orma-resumen-config-badge orma-resumen-config-badge--warn">
                            {formatInteger(appliedConfig.excluded_material_ax_groups.length)}
                          </span>
                        </span>
                      </>
                    )}
                  </div>
                )}

                {/* B — Bloque protagonista: valor a reportar + cobertura */}
                <div className="orma-valor-hero">
                  <div className="orma-valor-hero__main">
                    <div className="orma-valor-hero__label">Valor a reportar</div>
                    <div className="orma-valor-hero__amount">{formatCurrency(valuation.value)}</div>
                    <div className="orma-valor-hero__sub">
                      Suma de coste · {formatInteger(summary.included_rows)} filas contabilizadas
                    </div>
                    {calculating && (
                      <span className="orma-kpi-status orma-kpi-calculating" aria-live="polite">
                        Recalculando…
                      </span>
                    )}
                    {hasConsistencyIssue && (
                      <span className="orma-kpi-status orma-kpi-warn" role="alert">
                        <i className="bi bi-exclamation-triangle-fill" aria-hidden="true" /> Inconsistencia interna
                      </span>
                    )}
                  </div>
                  <div className="orma-valor-hero__coverage">
                    <div className="orma-valor-hero__label">Cobertura del cálculo</div>
                    <div className="orma-valor-hero__coverage-pct">{formatPercentage(summary.valuable_rows_percent)}</div>
                    <div className="orma-coverage-bar" role="presentation">
                      <div
                        className="orma-coverage-bar__fill"
                        style={{ width: `${Math.min(100, Math.max(0, Number(summary.valuable_rows_percent) || 0))}%` }}
                      />
                    </div>
                    <div className="orma-valor-hero__coverage-detail">
                      {formatInteger(summary.included_rows)} de {formatInteger(summary.in_scope_rows)} filas en alcance
                    </div>
                  </div>
                </div>

                {/* C — Conciliación: de la carga al importe */}
                <div className="orma-conciliacion">
                  <div className="orma-conciliacion__label">Conciliación · de la carga al importe</div>
                  <div className="orma-conciliacion__flow">
                    <div className="orma-conciliacion__step">
                      <div className="orma-conciliacion__num">{formatInteger(summary.total_rows_evaluated)}</div>
                      <div className="orma-conciliacion__name">Filas evaluadas</div>
                      <div className="orma-conciliacion__detail">Carga ORMA completa</div>
                    </div>
                    <div className="orma-conciliacion__arrow" aria-hidden="true">
                      <i className="bi bi-arrow-right" />
                    </div>
                    <div className="orma-conciliacion__step">
                      <div className="orma-conciliacion__num">{formatInteger(summary.in_scope_rows)}</div>
                      <div className="orma-conciliacion__name">En alcance</div>
                      <div className="orma-conciliacion__detail">Sucursal + almacén</div>
                    </div>
                    <div className="orma-conciliacion__arrow" aria-hidden="true">
                      <i className="bi bi-arrow-right" />
                    </div>
                    <div className="orma-conciliacion__step orma-conciliacion__step--final">
                      <div className="orma-conciliacion__num">{formatInteger(summary.included_rows)}</div>
                      <div className="orma-conciliacion__name">Contabilizadas</div>
                      <div className="orma-conciliacion__detail">Cuadra con el valor reportado</div>
                    </div>
                  </div>
                </div>

                {/* D — Tarjetas: Requiere revisión + Concentración del valor */}
                <div className="orma-summary-cards">
                  <div className="orma-summary-card orma-summary-card--review">
                    <h3 className="orma-summary-card__title">
                      <i className="bi bi-exclamation-circle" aria-hidden="true" /> Requiere revisión
                    </h3>
                    <dl className="orma-summary-card__list">
                      <div className={Number(summary.missing_master_rows) > 0 ? "orma-summary-card__row orma-summary-card__row--alert" : "orma-summary-card__row"}>
                        <dt>Sin MAESTRO</dt>
                        <dd>{formatInteger(summary.missing_master_rows)}</dd>
                      </div>
                      <div className={Number(quality.null_cost_rows) > 0 ? "orma-summary-card__row orma-summary-card__row--alert" : "orma-summary-card__row"}>
                        <dt>Costes nulos</dt>
                        <dd>{formatInteger(quality.null_cost_rows)}</dd>
                      </div>
                      <div className={Number(summary.invalid_cost_rows) > 0 ? "orma-summary-card__row orma-summary-card__row--alert" : "orma-summary-card__row"}>
                        <dt>Costes inválidos</dt>
                        <dd>{formatInteger(summary.invalid_cost_rows)}</dd>
                      </div>
                    </dl>
                    <p className="orma-summary-card__note">Detalle fila a fila en la pestaña Exclusiones.</p>
                  </div>

                  <div className="orma-summary-card orma-summary-card--concentration">
                    <h3 className="orma-summary-card__title">
                      <i className="bi bi-bar-chart-line" aria-hidden="true" /> Concentración del valor
                    </h3>
                    <dl className="orma-summary-card__list">
                      <div className="orma-summary-card__row">
                        <dt>Top 5</dt>
                        <dd>
                          <span className="orma-summary-card__money">{formatCurrency(quality.top_5_materials_value)}</span>
                          {" "}<span className="orma-summary-card__pct">({formatPercentage(quality.top_5_materials_percentage)})</span>
                        </dd>
                      </div>
                      <div className="orma-summary-card__row">
                        <dt>Top 10</dt>
                        <dd>
                          <span className="orma-summary-card__money">{formatCurrency(quality.top_10_materials_value)}</span>
                          {" "}<span className="orma-summary-card__pct">({formatPercentage(quality.top_10_materials_percentage)})</span>
                        </dd>
                      </div>
                      <div className="orma-summary-card__row">
                        <dt>Mayor valor</dt>
                        <dd className="orma-valuation-code">{formatText(quality.highest_value_material)}</dd>
                      </div>
                    </dl>
                  </div>
                </div>

                {warnings.length > 0 && (
                  <div className="orma-valuation-warnings orma-tabpanel-section" role="status">
                    <h3>Advertencias</h3>
                    <ul>
                      {warnings.map((warning, index) => (
                        <li key={`${index}-${String(warning?.message || warning)}`}>
                          {formatText(warning?.message || warning)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* PESTAÑA: EXCLUSIONES */}
            {activeTab === "exclusiones" && (
              <div
                role="tabpanel"
                id="orma-tabpanel-exclusiones"
                aria-labelledby="orma-tab-exclusiones"
                className="orma-tabpanel"
              >
                <div className="orma-valuation-table-wrap">
                  <table className="orma-valuation-table">
                    <caption className="visually-hidden">
                      Clasificaciones de exclusión dentro del alcance del almacén
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">Motivo</th>
                        <th scope="col" className="orma-valuation-number">Filas</th>
                        <th scope="col" className="orma-valuation-number">Materiales</th>
                        <th scope="col" className="orma-valuation-number">Coste conocido excluido</th>
                        <th scope="col" className="orma-valuation-number">Porcentaje</th>
                        <th scope="col">Detalle</th>
                      </tr>
                    </thead>
                    <tbody>
                      {EXCLUSION_ROWS.map(([key, label]) => {
                        const classification = classifications[key] || {};
                        return (
                          <tr key={key}>
                            <th scope="row">{label}</th>
                            <td className="orma-valuation-number">{formatInteger(classification.rows)}</td>
                            <td className="orma-valuation-number">{formatInteger(classification.distinct_materials)}</td>
                            <td className="orma-valuation-number orma-valuation-money">
                              {classification.known_value === null
                                ? "No disponible"
                                : formatCurrency(classification.known_value)}
                            </td>
                            <td className="orma-valuation-number">—</td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-secondary orma-valuation-detail-action"
                                onClick={(event) => openDetail(key, event.currentTarget)}
                              >
                                Ver detalle
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <details className="orma-tech-metrics">
                  <summary className="orma-tech-metrics__summary">Métricas técnicas de calidad del dato</summary>
                  <div className="orma-tech-metrics__body">
                    <div className="orma-valuation-quality-layout">
                      <div className="orma-valuation-quality-block">
                        <h3>Rango de costes</h3>
                        <dl className="orma-valuation-quality-grid">
                          <div><dt>Coste mínimo</dt><dd className="orma-valuation-money">{formatCurrency(quality.minimum_cost)}</dd></div>
                          <div><dt>Coste máximo</dt><dd className="orma-valuation-money">{formatCurrency(quality.maximum_cost)}</dd></div>
                        </dl>
                      </div>
                      <div className="orma-valuation-quality-block">
                        <h3>Coberturas globales</h3>
                        <dl className="orma-valuation-quality-grid">
                          <div><dt>Cobertura MAESTRO</dt><dd>{formatPercentage(coverage.master_coverage_percent)}</dd></div>
                          <div><dt>Cobertura AX</dt><dd>{formatPercentage(coverage.ax_coverage_over_total_percent)}</dd></div>
                        </dl>
                        <p>Sobre el conjunto global de la carga ORMA.</p>
                      </div>
                      <div className="orma-valuation-quality-block">
                        <h3>Incidencias de costes</h3>
                        <dl className="orma-valuation-quality-grid">
                          <div><dt>Costes nulos</dt><dd>{formatInteger(quality.null_cost_rows)}</dd></div>
                          <div><dt>Costes no finitos</dt><dd>{formatInteger(quality.non_finite_cost_rows)}</dd></div>
                          <div><dt>Costes negativos</dt><dd>{formatInteger(quality.negative_cost_rows)}</dd></div>
                        </dl>
                      </div>
                      <div className="orma-valuation-quality-block orma-valuation-quality-neutral">
                        <h3>Costes cero</h3>
                        <dl><div><dt>Filas con coste cero</dt><dd>{formatInteger(quality.zero_cost_rows)}</dd></div></dl>
                        <p>Datos válidos — no se clasifican automáticamente como error.</p>
                      </div>
                    </div>
                  </div>
                </details>
              </div>
            )}

            {/* PESTAÑA: GRUPOS AX */}
            {activeTab === "grupos-ax" && (
              <div
                role="tabpanel"
                id="orma-tabpanel-grupos-ax"
                aria-labelledby="orma-tab-grupos-ax"
                className="orma-tabpanel"
              >
                {byAxGroup.length === 0 ? (
                  <div className="orma-valuation-analytic-empty" role="status">
                    No hay datos de grupos AX para mostrar.
                  </div>
                ) : (
                  <div className="orma-valuation-table-wrap">
                    <table className="orma-valuation-table">
                      <caption className="visually-hidden">Valor incluido desglosado por grupo AX</caption>
                      <thead>
                        <tr>
                          <th scope="col">Grupo AX</th>
                          <th scope="col" className="orma-valuation-number">Valor</th>
                          <th scope="col" className="orma-valuation-number">Filas</th>
                          <th scope="col" className="orma-valuation-number">Materiales</th>
                          <th scope="col" className="orma-valuation-number">% del valor incluido</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byAxGroup.map((row, index) => (
                          <tr key={`${String(row?.key)}-${index}`}>
                            <th scope="row" className="orma-valuation-code">{formatText(row?.key)}</th>
                            <td className="orma-valuation-number orma-valuation-money">{formatCurrency(row?.value)}</td>
                            <td className="orma-valuation-number">{formatInteger(row?.rows)}</td>
                            <td className="orma-valuation-number">{formatInteger(row?.distinct_materials)}</td>
                            <td className="orma-valuation-number">{formatPercentage(row?.percentage_of_included_value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* PESTAÑA: FAMILIAS */}
            {activeTab === "familias" && (
              <div
                role="tabpanel"
                id="orma-tabpanel-familias"
                aria-labelledby="orma-tab-familias"
                className="orma-tabpanel"
              >
                {byFamily.length === 0 ? (
                  <div className="orma-valuation-analytic-empty" role="status">
                    No hay datos de familias para mostrar.
                  </div>
                ) : (
                  <div className="orma-valuation-table-wrap">
                    <table className="orma-valuation-table">
                      <caption className="visually-hidden">Valor incluido desglosado por familia</caption>
                      <thead>
                        <tr>
                          <th scope="col">Familia</th>
                          <th scope="col" className="orma-valuation-number">Valor</th>
                          <th scope="col" className="orma-valuation-number">Filas</th>
                          <th scope="col" className="orma-valuation-number">Materiales</th>
                          <th scope="col" className="orma-valuation-number">% del valor incluido</th>
                        </tr>
                      </thead>
                      <tbody>
                        {byFamily.map((row, index) => (
                          <tr key={`${String(row?.key)}-${index}`}>
                            <th scope="row">{formatText(row?.key, "Sin familia")}</th>
                            <td className="orma-valuation-number orma-valuation-money">{formatCurrency(row?.value)}</td>
                            <td className="orma-valuation-number">{formatInteger(row?.rows)}</td>
                            <td className="orma-valuation-number">{formatInteger(row?.distinct_materials)}</td>
                            <td className="orma-valuation-number">{formatPercentage(row?.percentage_of_included_value)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* PESTAÑA: MATERIALES */}
            {activeTab === "materiales" && (
              <div
                role="tabpanel"
                id="orma-tabpanel-materiales"
                aria-labelledby="orma-tab-materiales"
                className="orma-tabpanel"
              >
                <div className="orma-materials-toggle" role="group" aria-label="Vista de materiales">
                  <button
                    type="button"
                    className={`orma-materials-toggle__btn${materialsView === "included" ? " orma-materials-toggle__btn--active" : ""}`}
                    onClick={() => setMaterialsView("included")}
                    aria-pressed={materialsView === "included"}
                  >
                    Filas incluidas
                  </button>
                  <button
                    type="button"
                    className={`orma-materials-toggle__btn${materialsView === "excluded" ? " orma-materials-toggle__btn--active" : ""}`}
                    onClick={() => setMaterialsView("excluded")}
                    aria-pressed={materialsView === "excluded"}
                  >
                    Filas excluidas
                  </button>
                </div>

                {materialsView === "included" && (
                  <>
                    <div className="orma-materials-header">
                      <span className="orma-materials-header__title">Materiales incluidos · mayor valor</span>
                      <span className="orma-materials-header__count">
                        {formatInteger(summary.included_rows)} filas contabilizadas · total {formatCurrency(valuation.value)}
                      </span>
                    </div>
                    <p className="orma-materials-note">
                      Lista de materiales con mayor peso en el valor reportado. El total cuadra con el importe de portada. Accede al detalle completo fila a fila desde la pestaña Exclusiones.
                    </p>
                    {topMaterials.length === 0 ? (
                      <div className="orma-valuation-analytic-empty" role="status">
                        No hay materiales incluidos para mostrar.
                      </div>
                    ) : (
                      <div className="orma-valuation-table-wrap">
                        <table className="orma-valuation-table">
                          <caption className="visually-hidden">Materiales con mayor valor incluido</caption>
                          <thead>
                            <tr>
                              <th scope="col">ID material</th>
                              <th scope="col">Descripción</th>
                              <th scope="col" className="orma-valuation-number">Valor</th>
                              <th scope="col" className="orma-valuation-number">% del total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {topMaterials.map((row, index) => (
                              <tr key={`${String(row?.material_id)}-${index}`}>
                                <th scope="row" className="orma-valuation-code">{formatText(row?.material_id)}</th>
                                <td className="orma-mat-desc" title={formatText(row?.description)}>{formatText(row?.description)}</td>
                                <td className="orma-valuation-number orma-valuation-money">{formatCurrency(row?.value)}</td>
                                <td className="orma-valuation-number orma-mat-pct-td">
                                  <div>{formatPercentage(row?.percentage_of_included_value)}</div>
                                  <div className="orma-mat-bar" role="presentation">
                                    <div
                                      className="orma-mat-bar__fill"
                                      style={{ width: `${Math.min(100, Math.max(0, Number(row?.percentage_of_included_value) || 0))}%` }}
                                    />
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}

                {materialsView === "excluded" && (
                  <>
                    <div className="orma-materials-header">
                      <span className="orma-materials-header__title">Recuento de exclusiones por motivo</span>
                      <span className="orma-materials-header__count">
                        {formatInteger(summary.excluded_in_scope_rows)} filas excluidas dentro del alcance
                      </span>
                    </div>
                    <p className="orma-materials-note">
                      Resumen de filas que no entran en el cálculo, agrupadas por motivo. No es una lista de materiales — el detalle fila a fila está en la pestaña Exclusiones.
                    </p>
                    <div className="orma-valuation-table-wrap">
                      <table className="orma-valuation-table">
                        <caption className="visually-hidden">Recuento de filas excluidas por motivo</caption>
                        <thead>
                          <tr>
                            <th scope="col">Motivo de exclusión</th>
                            <th scope="col" className="orma-valuation-number">Filas</th>
                            <th scope="col" className="orma-valuation-number">Materiales</th>
                            <th scope="col" className="orma-valuation-number">Coste conocido excluido</th>
                          </tr>
                        </thead>
                        <tbody>
                          {EXCLUSION_ROWS.map(([key, label]) => {
                            const cls = classifications[key] || {};
                            return (
                              <tr key={key}>
                                <th scope="row">{label}</th>
                                <td className="orma-valuation-number">{formatInteger(cls.rows)}</td>
                                <td className="orma-valuation-number">{formatInteger(cls.distinct_materials)}</td>
                                <td className="orma-valuation-number orma-valuation-money">
                                  {cls.known_value === null ? "No disponible" : formatCurrency(cls.known_value)}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        optionsState.status === "ready" && (
          <div className="orma-valuation-empty-state" role="status">
            Configure las condiciones y pulse <strong>Aplicar y recalcular</strong>.
          </div>
        )
      )}

      {/* ── MODAL DE DETALLE (sin cambios) ──────────────── */}
      {detailState.isOpen && (
        <div className="orma-valuation-detail-backdrop">
          <div
            ref={detailModalElementRef}
            className="orma-valuation-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="orma-detail-title"
            aria-busy={detailState.status === "loading"}
            tabIndex={-1}
          >
            <header className="orma-valuation-detail-header">
              <div>
                <h2 id="orma-detail-title">
                  {DETAIL_CLASSIFICATION_LABELS[detailState.classification] ||
                    "Detalle justificativo"}
                </h2>
                <p>
                  Clasificación: <code>{formatText(detailState.classification)}</code>
                </p>
              </div>
              <button
                ref={detailCloseButtonRef}
                type="button"
                className="btn btn-secondary orma-valuation-detail-close"
                onClick={closeDetail}
              >
                Cerrar
              </button>
            </header>

            <div className="orma-valuation-detail-body">
              {isConfigDirty && (
                <div className="orma-valuation-detail-applied-notice" role="status">
                  Este detalle corresponde a la última configuración aplicada, no a los controles
                  actualmente editados.
                </div>
              )}

              <section
                className="orma-valuation-detail-config"
                aria-labelledby="orma-detail-config-title"
              >
                <h3 id="orma-detail-config-title">Configuración aplicada</h3>
                <dl className="orma-valuation-detail-config-grid">
                  <div><dt>Sucursal</dt><dd>{formatText(detailState.config?.branch_prefix)}</dd></div>
                  <div><dt>Almacén</dt><dd>{formatText(detailState.config?.warehouse)}</dd></div>
                  <div><dt>Exclusión de sufijo U</dt><dd>{detailState.config?.exclude_suffix_u ? "Sí" : "No"}</dd></div>
                  <div><dt>Umbral R</dt><dd>{formatText(detailState.config?.suffix_r_min_cost)}</dd></div>
                  <div><dt>Grupos AX excluidos</dt><dd>{formatInteger(detailState.config?.excluded_material_ax_groups?.length)}</dd></div>
                </dl>
              </section>

              <form className="orma-valuation-detail-filters" onSubmit={applyDetailFilters}>
                <div className="orma-valuation-detail-section-heading">
                  <div>
                    <h3>Filtros</h3>
                    <p>{Object.keys(detailState.appliedFilters).length} filtros aplicados</p>
                  </div>
                </div>
                <div className="orma-valuation-detail-filter-grid">
                  {DETAIL_FILTER_FIELDS.map(([key, label]) => (
                    <div className="orma-valuation-detail-field" key={key}>
                      <label htmlFor={`orma-detail-filter-${key}`}>{label}</label>
                      <input
                        id={`orma-detail-filter-${key}`}
                        type="text"
                        value={detailState.draftFilters[key] || ""}
                        onChange={(event) => updateDetailDraftFilter(key, event.target.value)}
                        disabled={detailState.status === "loading"}
                      />
                    </div>
                  ))}
                </div>
                <div className="orma-valuation-detail-filter-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={detailState.status === "loading"}
                  >
                    Aplicar filtros
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={clearDetailFilters}
                    disabled={detailState.status === "loading"}
                  >
                    Limpiar filtros
                  </button>
                </div>
              </form>

              <div className="orma-valuation-detail-toolbar">
                <div className="orma-valuation-detail-field">
                  <label htmlFor="orma-detail-sort-by">Ordenar por</label>
                  <select
                    id="orma-detail-sort-by"
                    value={detailState.sortBy}
                    onChange={(event) =>
                      changeDetailSort(event.target.value, detailState.sortOrder)
                    }
                    disabled={detailState.status === "loading"}
                  >
                    {DETAIL_SORT_OPTIONS.map(([value, label]) => (
                      <option value={value} key={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="orma-valuation-detail-field">
                  <label htmlFor="orma-detail-sort-order">Dirección</label>
                  <select
                    id="orma-detail-sort-order"
                    value={detailState.sortOrder}
                    onChange={(event) => changeDetailSort(detailState.sortBy, event.target.value)}
                    disabled={detailState.status === "loading"}
                  >
                    <option value="asc">Ascendente</option>
                    <option value="desc">Descendente</option>
                  </select>
                </div>
                <div className="orma-valuation-detail-field">
                  <label htmlFor="orma-detail-per-page">Filas por página</label>
                  <select
                    id="orma-detail-per-page"
                    value={detailState.pagination.per_page}
                    onChange={(event) => changeDetailPerPage(event.target.value)}
                    disabled={detailState.status === "loading"}
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                <div className="orma-valuation-detail-generated">
                  <span>Generado</span>
                  <strong>{formatDate(detailState.generatedAt)}</strong>
                </div>
              </div>

              {detailState.status === "loading" && (
                <div
                  className="orma-valuation-detail-state orma-valuation-detail-loading"
                  aria-live="polite"
                >
                  Cargando detalle justificativo…
                </div>
              )}

              {detailState.status === "error" && (
                <div
                  className="orma-valuation-detail-state orma-valuation-detail-error"
                  role="alert"
                >
                  <p>{detailState.error}</p>
                  <button type="button" className="btn btn-secondary" onClick={retryDetail}>
                    Reintentar
                  </button>
                </div>
              )}

              {detailState.status === "empty" && (
                <div
                  className="orma-valuation-detail-state orma-valuation-detail-empty"
                  role="status"
                >
                  No hay filas para los filtros y la clasificación seleccionados.
                </div>
              )}

              {detailState.items.length > 0 && (
                <div className="orma-valuation-detail-table-wrap">
                  <table className="orma-valuation-detail-table">
                    <caption className="visually-hidden">
                      Filas justificativas de la clasificación seleccionada
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col" aria-sort={getDetailAriaSort("material_id")}>ID material</th>
                        <th scope="col" aria-sort={getDetailAriaSort("description")}>Descripción</th>
                        <th scope="col" aria-sort={getDetailAriaSort("cost")} className="orma-valuation-number">Coste</th>
                        <th scope="col" aria-sort={getDetailAriaSort("quantity")} className="orma-valuation-number">Cantidad auxiliar</th>
                        <th scope="col">Explicación</th>
                        <th scope="col" aria-sort={getDetailAriaSort("branch_name")}>Sucursal</th>
                        <th scope="col" aria-sort={getDetailAriaSort("warehouse")}>Almacén</th>
                        <th scope="col">Columna</th>
                        <th scope="col">Estante</th>
                        <th scope="col">Bin</th>
                        <th scope="col">Segunda ubicación</th>
                        <th scope="col" aria-sort={getDetailAriaSort("family")}>Familia</th>
                        <th scope="col" aria-sort={getDetailAriaSort("brand")}>Marca</th>
                        <th scope="col">Modelo</th>
                        <th scope="col" aria-sort={getDetailAriaSort("id_material_ax")}>Grupo AX</th>
                        <th scope="col">Coincidencia MAESTRO</th>
                        <th scope="col">Clasificación</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailState.items.map((item, index) => (
                        <tr key={`${String(item?.material_id)}-${index}`}>
                          <th scope="row" className="orma-valuation-code">{formatText(item?.material_id)}</th>
                          <td>{formatText(item?.description)}</td>
                          <td className="orma-valuation-number orma-valuation-money">{formatCurrency(item?.cost)}</td>
                          <td className="orma-valuation-number">{formatNumber(item?.quantity)}</td>
                          <td>{formatText(item?.explanation)}</td>
                          <td>{formatText(item?.branch_name)}</td>
                          <td>{formatText(item?.warehouse)}</td>
                          <td>{formatText(item?.column_location)}</td>
                          <td>{formatText(item?.shelf)}</td>
                          <td>{formatText(item?.bin)}</td>
                          <td>{formatText(item?.second_location)}</td>
                          <td>{formatText(item?.family)}</td>
                          <td>{formatText(item?.brand)}</td>
                          <td>{formatText(item?.model)}</td>
                          <td className="orma-valuation-code">{formatText(item?.id_material_ax)}</td>
                          <td>{item?.has_master_match === true ? "Sí" : item?.has_master_match === false ? "No" : "—"}</td>
                          <td>{formatText(item?.classification)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <nav className="orma-valuation-detail-pagination" aria-label="Paginación del detalle">
                <div className="orma-valuation-detail-pagination-summary" aria-live="polite">
                  <span>
                    Página {detailState.pagination.total_pages === 0 ? 0 : formatInteger(detailState.pagination.page)} de {formatInteger(detailState.pagination.total_pages)}
                  </span>
                  <span>{formatInteger(detailState.pagination.total_items)} resultados</span>
                </div>
                <div className="orma-valuation-detail-pagination-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => goToDetailPage(1)} disabled={detailState.status === "loading" || detailState.pagination.total_pages === 0 || detailState.pagination.page <= 1}>Primera</button>
                  <button type="button" className="btn btn-secondary" onClick={() => goToDetailPage(detailState.pagination.page - 1)} disabled={detailState.status === "loading" || detailState.pagination.total_pages === 0 || detailState.pagination.page <= 1}>Anterior</button>
                  <button type="button" className="btn btn-secondary" onClick={() => goToDetailPage(detailState.pagination.page + 1)} disabled={detailState.status === "loading" || detailState.pagination.total_pages === 0 || detailState.pagination.page >= detailState.pagination.total_pages}>Siguiente</button>
                  <button type="button" className="btn btn-secondary" onClick={() => goToDetailPage(detailState.pagination.total_pages)} disabled={detailState.status === "loading" || detailState.pagination.total_pages === 0 || detailState.pagination.page >= detailState.pagination.total_pages}>Última</button>
                </div>
              </nav>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default OrmaWarehouseValuation;
